import { Input, InputNumber, Select, Switch, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiChevronDown, BiChevronUp, BiPlus, BiTrash } from "react-icons/bi";
import { NAME_SPACES } from "shared/locales/constants";
import {
  TRANSFORM_BY_OP,
  TRANSFORMS,
} from "screens/Private/Admin/DynamicDocuments/constants";

const KeyValueField = ({ value = {}, onChange, labels }) => {
  const [rows, setRows] = useState(() => Object.entries(value));

  const asTable = (pairs) =>
    pairs.reduce((table, [key, label]) => {
      if (key) table[key] = label;
      return table;
    }, {});

  useEffect(() => {
    if (JSON.stringify(asTable(rows)) !== JSON.stringify(value || {}))
      setRows(Object.entries(value || {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = (next) => {
    setRows(next);
    onChange(asTable(next));
  };

  return (
    <div>
      {rows.map(([key, label], idx) => (
        <div key={idx} className="dynamic-documents__source-row">
          <Input
            size="small"
            placeholder={labels.KEY_PLACEHOLDER}
            className="dynamic-documents__field-mono"
            value={key}
            onChange={(e) =>
              commit(
                rows.map((row, i) =>
                  i === idx ? [e.target.value, row[1]] : row,
                ),
              )
            }
          />
          <span className="dynamic-documents__match-arrow">→</span>
          <Input
            size="small"
            placeholder={labels.LABEL_PLACEHOLDER}
            value={label}
            onChange={(e) =>
              commit(
                rows.map((row, i) =>
                  i === idx ? [row[0], e.target.value] : row,
                ),
              )
            }
          />
          <button
            type="button"
            className="conditions-editor__remove-btn"
            onClick={() => commit(rows.filter((_, i) => i !== idx))}
          >
            <BiTrash size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="conditions-editor__add-btn"
        onClick={() => commit([...rows, ["", ""]])}
      >
        <BiPlus size={14} /> {labels.ADD}
      </button>
    </div>
  );
};

const TransformField = ({ field, value, onChange, texts, matchLabels }) => {
  if (field.type === "number")
    return (
      <InputNumber
        size="small"
        className="dynamic-documents__field-full"
        placeholder={field.default != null ? String(field.default) : undefined}
        value={value ?? null}
        onChange={(next) => onChange(next ?? undefined)}
      />
    );

  if (field.type === "boolean")
    return (
      <Switch
        size="small"
        checked={!!value}
        onChange={(next) => onChange(next || undefined)}
      />
    );

  if (field.type === "select")
    return (
      <Select
        size="small"
        allowClear
        showSearch={field.allowCustom}
        className="dynamic-documents__field-full"
        placeholder={field.default}
        value={value ?? undefined}
        onChange={(next) => onChange(next ?? undefined)}
        options={(field.options || []).map(({ value: optionValue, key }) => ({
          value: optionValue,
          label: texts.OPTIONS?.[key] ?? optionValue,
        }))}
        {...(field.allowCustom
          ? {
              onSearch: undefined,
              filterOption: (input, option) =>
                (option.value || "")
                  .toLowerCase()
                  .includes(input.toLowerCase()),
            }
          : {})}
      />
    );

  if (field.type === "keyValue")
    return (
      <KeyValueField value={value} onChange={onChange} labels={matchLabels} />
    );

  return (
    <Input
      size="small"
      className="dynamic-documents__field-full"
      placeholder={field.default != null ? String(field.default) : undefined}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || undefined)}
    />
  );
};

const TransformsEditor = ({ transforms = [], onChange }) => {
  const { t } = useTranslation(NAME_SPACES.COMPONENTS);
  const TRANSFORMS_EDITOR = t("TRANSFORMS_EDITOR", { returnObjects: true });
  const OPS = TRANSFORMS_EDITOR.OPS;

  const OP_OPTIONS = TRANSFORMS.map(({ op, key }) => ({
    value: op,
    label: OPS[key].LABEL,
  }));

  const update = (idx, patch) =>
    onChange(transforms.map((t_, i) => (i === idx ? { ...t_, ...patch } : t_)));

  const remove = (idx) => onChange(transforms.filter((_, i) => i !== idx));

  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= transforms.length) return;
    const next = [...transforms];
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  const add = () => onChange([...transforms, { op: "date" }]);

  const changeOp = (idx, op) =>
    onChange(transforms.map((t_, i) => (i === idx ? { op } : t_)));

  return (
    <div className="conditions-editor">
      <div className="conditions-editor__header">
        <span className="conditions-editor__title">
          {TRANSFORMS_EDITOR.TITLE}
        </span>
      </div>

      {transforms.length === 0 && (
        <p className="conditions-editor__empty">{TRANSFORMS_EDITOR.EMPTY}</p>
      )}

      <div className="conditions-editor__rules">
        {transforms.map((transform, idx) => {
          const definition = TRANSFORM_BY_OP[transform.op];
          const texts = definition ? OPS[definition.key] : null;
          return (
            <div key={idx} className="conditions-editor__rule">
              {idx > 0 && (
                <div className="conditions-editor__rule-connector">
                  {TRANSFORMS_EDITOR.THEN}
                </div>
              )}
              <div className="conditions-editor__rule-row">
                <Select
                  size="small"
                  className="conditions-editor__field-select"
                  value={transform.op}
                  onChange={(op) => changeOp(idx, op)}
                  options={OP_OPTIONS}
                />
                <Tooltip title={TRANSFORMS_EDITOR.MOVE_UP}>
                  <button
                    type="button"
                    disabled={idx === 0}
                    onClick={() => move(idx, -1)}
                    className="conditions-editor__remove-btn"
                  >
                    <BiChevronUp size={14} />
                  </button>
                </Tooltip>
                <Tooltip title={TRANSFORMS_EDITOR.MOVE_DOWN}>
                  <button
                    type="button"
                    disabled={idx === transforms.length - 1}
                    onClick={() => move(idx, 1)}
                    className="conditions-editor__remove-btn"
                  >
                    <BiChevronDown size={14} />
                  </button>
                </Tooltip>
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="conditions-editor__remove-btn"
                >
                  <BiTrash size={14} />
                </button>
              </div>

              {texts?.HINT && (
                <div className="dynamic-documents__config-field-hint">
                  {texts.HINT}
                </div>
              )}

              {(definition?.fields || []).map((field) => {
                const fieldTexts = texts?.FIELDS?.[field.key] || {};
                return (
                  <div
                    key={field.name}
                    className="dynamic-documents__transform-field"
                  >
                    <div className="dynamic-documents__config-field-label">
                      {fieldTexts.LABEL}
                    </div>
                    <TransformField
                      field={field}
                      value={transform[field.name]}
                      texts={texts || {}}
                      matchLabels={TRANSFORMS_EDITOR.MATCH}
                      onChange={(next) => update(idx, { [field.name]: next })}
                    />
                    {fieldTexts.HINT && (
                      <div className="dynamic-documents__config-field-hint">
                        {fieldTexts.HINT}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={add}
        className="conditions-editor__add-btn"
      >
        <BiPlus size={14} /> {TRANSFORMS_EDITOR.ADD}
      </button>
    </div>
  );
};

export default TransformsEditor;
